import React, { useEffect, useMemo, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import '../styles/dashboard.css';
import Apis from '../services/apis';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [load, setLoad] = useState(true);
  const apis = new Apis();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  /* ===================== FETCH ===================== */
  useEffect(() => {
    getUsers()
  }, []);


  const getUsers = () => {
    apis.getusers()
    .then((response) => {
        let data = response.data.data.users
        let arr = [];
        data.forEach(user => {
          if(user.roles.filter(r => r.name === "Terminal Client").length>0)
          {
            arr.push({
              id: user.id,
              name: user.name,
            })
          }
        });
        // setClients(arr)
        // setLoad(false)
        let users = arr || []
        fetch('https://schedulerapi.pricingculture.com/holdings/getSummary')
        .then(res => res.json())
        .then((res)=>{
          console.log(users)
          res?.topHoldings?.map((item)=>{
            item["clientName"] = users.find(u=>u.id.toString()===item.clientId.toString())?.name || item.clientId
          })

          setData(res)
          setLoad(false)
        })
        .catch(console.error);
    })
    .catch((error) => {
        if (error.response && error.response.status === 404) {
            setLoad(false)
            alert("Error occurred while fetching categories "+ error.response.data.message);
        } else {
            // Handle other errors
            console.error("Error:", error);
            alert("Error occurred while fetching asset data");
        }
    });
  }



  /* ===================== SAFE DEFAULTS ===================== */
  const summary = data?.summary;
  const byCategory = data?.byCategory || [];
  const topHoldings = data?.topHoldings
  ?.slice() // prevent mutation
  .sort((a, b) => b.currentValue - a.currentValue) || [];

  /* ===================== FORMATTERS ===================== */
  const formatCurrency = (value = 0) => {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  /* ===================== MEMOIZED DATA ===================== */
  const assetData = useMemo(() => {
    if (!byCategory.length) return [];

    const total = byCategory.reduce((sum, c) => sum + c.holdingValue, 0);

    let res =  byCategory.map(c => ({
      name: c.label,
      percent: Number(((c.holdingValue / total) * 100).toFixed(1)),
      value: c.holdingValue,
      color: c.color,
    }));
    res.sort((a, b) => b.percent - a.percent);
    return res;
  }, [byCategory]);

  const topCategory = useMemo(() => {
    if (!assetData.length) return null;
    return [...assetData].sort((a, b) => b.percent - a.percent)[0];
  }, [assetData]);




  /* ===================== METRICS ===================== */
  const metrics = useMemo(() => [
    
    // {
    //   label: 'Total Assets',
    //   value: summary?.totalAssets ?? '-',
    //   // change: summary ? `${summary.totalHoldings} holdings` : '',
    //   positive: true,
    // },
   
    // {
    //   label: 'Active Clients',
    //   value: summary?.totalClients ?? '-',
    //   change: '+',
    //   positive: true,
    // },
    {
      label: 'Total Assets',
      value: summary ? formatCurrency(summary.totalValue) : '-',
      change: '',
      positive: true,
    },
    {
      label: 'Active Clients',
      value: summary?.totalClients ?? '-',
      // change: summary ? `${summary.totalHoldings} holdings` : '',
      positive: true,
    },
    {
      label: 'Total Holdings',
      value: summary?.totalHoldings ?? '-',
      // Value: summary ? `${summary.totalHoldings} holdings` : '',
      positive: true,
    },
    {
      label: 'Top Asset Class',
      value: topCategory?.name ?? '-',
      change: topCategory ? `${topCategory.percent}%` : '',
      positive: true,
    },
  ], [summary, topCategory]);

  /* ===================== COLOR UTILITY ===================== */
  const adjustColor = (hex, amount) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 255) + amount));
    const b = Math.min(255, Math.max(0, (num & 255) + amount));
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  };

  /* ===================== LOADING ===================== */
  if (load) {
    return (
      <div style={{
        height: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div
          style={{
            width: '80px',
            height: '80px',
            border: '10px solid rgba(255,255,255,0.2)',
            borderTop: '10px solid #F4A31E',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
        />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="subtitle">Overview of all client assets</p>
      </div>

      {/* ===================== METRICS ===================== */}
      <div className="metrics-grid">
        {metrics.map((m, i) => (
          <div key={i} className="metric-card">
            <div className="metric-label">{m.label}</div>
            <div className="metric-value">{m.value}</div>
            {m.change&&(<div 
            className={`metric-change ${m.positive ? 'positive' : 'negative'}`}
            // className="asset-icon" style={{padding:10}}
            >
              {m.change}
            </div>)}
          </div>
        ))}
      </div>

      <div className="content-grid">
        {/* ===================== PIE ===================== */}
        <div className="chart-section">
          <div className="section-header">
            <div style={{fontSize:'20px'}}>Asset Allocation</div>
            <div className="total-assets">
              <span className="total-label">Total Value</span>
              <span className="total-value">
                {formatCurrency(summary.totalValue)}
              </span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <defs>
                {assetData.map((e, i) => (
                  <linearGradient key={i} id={`grad-${i}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={adjustColor(e.color, 35)} />
                    <stop offset="100%" stopColor={adjustColor(e.color, -35)} />
                  </linearGradient>
                ))}
              </defs>

              <Pie
                data={assetData}
                dataKey="percent"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={140}
                paddingAngle={3}
                cornerRadius={8}
                activeIndex={activeIndex}
                activeOuterRadius={155}
                onMouseEnter={(_, i) => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {assetData.map((e, i) => (
                  <Cell
                    key={i}
                    fill={`url(#grad-${i})`}
                    opacity={activeIndex === null || activeIndex === i ? 1 : 0.3}
                    stroke="#020617"
                  />
                ))}
              </Pie>

              {activeIndex !== null ? (
                <>
                  <text x="50%" y="46%" textAnchor="middle" fill="#fff" fontSize={18} fontWeight={700}>
                    {formatCurrency(assetData[activeIndex].value)}
                  </text>
                  <text x="50%" y="54%" textAnchor="middle" fill="#94a3b8" fontSize={12}>
                    {assetData[activeIndex].name.toUpperCase()}
                  </text>
                </>
              ) : (
                <>
                  <text x="50%" y="46%" textAnchor="middle" fill="#fff" fontSize={18} fontWeight={700}>
                    {formatCurrency(summary.totalValue)}
                  </text>
                  <text x="50%" y="54%" textAnchor="middle" fill="#64748b" fontSize={12}>
                    TOTAL ASSETS
                  </text>
                </>
              )}
            </PieChart>
          </ResponsiveContainer>

          <div className="legend-custom">
            {assetData.map((i, idx) => (
              <div key={idx} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: i.color }} />
                <div className="legend-label">{i.name}</div>
                <div className="legend-value">{i.percent}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* ===================== TOP HOLDINGS ===================== */}
        <div className="holdings-section">
          <div className="section-header">
          <div style={{fontSize:'20px'}}>Top Holdings</div>

          </div>

          {/* <div className="holdings-table">
            <div className="table-header">
            <div className="col-asset">Client</div>
              <div className="col-asset">Asset</div>
              <div className="col-class">Asset Class</div>
              <div className="col-price">Value</div>
            </div>

            {topHoldings.map((h, i) => (
              <div key={h.holdingId} className="table-row">
                <div className="col-asset">
                  <div className="asset-icon">{h.clientId}</div>
                  <div className="asset-name">{h.assetName}</div>
                </div>
                <div
                  className="category-badge col-class"
                  style={{
                    marginTop:'15px',
                  
                    backgroundColor: `${h.categoryColor}22`,
                    color: h.categoryColor,
                    borderColor: `${h.categoryColor}`
                  }}
                >
                
                  <span style={{color:`${h.categoryColor}`}}>{h.categoryLabel}</span>
                </div>

                <div className="col-price">
                  {formatCurrency(h.currentValue)}
                </div>
              </div>
            ))}
          </div> */}

          <div className="holdings-table" style={{maxWidth:isMobile&&'100px'}}>
            <table className="holdings-table-el" >
              <thead>
                <tr>
                  {/* {!isMobile&&(<th>Client</th>)} */}
                  <th>Asset</th>
                  {/* <th>Asset Class</th> */}
                   <th>Client Account</th>
                  <th>Value</th>
                </tr>
              </thead>

              <tbody>
                {topHoldings.map((h) => (
                  <tr key={h.holdingId}>
                    {/* Client */}
                    {/* {!isMobile&&(<td className="client-cell">
                      <div className="asset-icon">{h.clientId}</div>
                    </td>)} */}

                    {/* Asset */}
                    <td>
                      <div className="asset-name" style={{ marginBottom: '-5px'}}>{h.assetName}</div>

                      <div
                        className="category-badge"
                        style={{
                          marginBottom: '0px',
                          backgroundColor: `${h.categoryColor}22`,
                          color: h.categoryColor,
                          border: `1px solid ${h.categoryColor}`,
                          fontSize: '8px',
                        }}
                      >
                        {h.categoryLabel}
                      </div>
                    </td>

                    <td>
                      {h.clientName}
                    </td>

                    {/* Asset Class */}
                    {/* <td>
                      <span
                        className="category-badge"
                        style={{
                          backgroundColor: `${h.categoryColor}22`,
                          color: h.categoryColor,
                          border: `1px solid ${h.categoryColor}`,
                        }}
                      >
                        {h.categoryLabel}
                      </span>
                    </td> */}

                    {/* Value */}
                    <td >
                      {formatCurrency(h.currentValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
